import PagedSearchTable, { PagedTableFunctions } from '../../components/PagedSearchTable'
import React, { useContext, useRef, useState } from 'react'
import Dialog from '../../components/Dialog'
import { EditRow, EditTable, focusFirstInput } from '../../components/Fields'
import AppContext from '../../appContext'
import LimeController from '../../controllers/LimeController'
import LimingRegionResponse from '../../controllers/LimingRegionResponse'
import LimingRegionRequest from '../../controllers/LimingRegionRequest'
import LabInputUnit from '../../controllers/LabInputUnit'
import MeasurementUnit from '../../controllers/MeasurementUnit'
import { useValidation } from '../../validation'
import { buildSetter } from '../../immutableState'
import Input from '../../components/Input'
import SelectString from '../../components/SelectString'
import { showSuccessOrFailed } from '../../Snacks'

const labInputUnitOptions = Object.values(LabInputUnit).map(u => ({ id: u, name: u }))

function labInputUnitDisplay(unit: LabInputUnit, system: MeasurementUnit): string {
    if (unit === LabInputUnit.Meq100g) return 'meq/100g'
    return system === MeasurementUnit.METRIC ? 'mg/kg' : 'ppm'
}

const emptyRegion: LimingRegionRequest = {
    id: 0,
    name: '',
    labInputUnit: LabInputUnit.MgKgPpm
}

function toForm(item: LimingRegionResponse): LimingRegionRequest {
    return {
        id: item.id,
        name: item.name,
        labInputUnit: item.labInputUnit
    }
}

const LimingRegions: React.FC = () => {
    const context = useContext(AppContext)
    const system = context.initial.system
    const pagedTableRef = useRef<PagedTableFunctions<LimingRegionResponse>>()
    const [show, setShow] = useState(false)
    const [upsertData, setUpsertData] = useState<LimingRegionRequest>(emptyRegion)
    const setData = buildSetter(upsertData, setUpsertData)

    function showPopup(preset: LimingRegionRequest) {
        setUpsertData(preset)
        setShow(true)
    }

    function upsertCallback(data: LimingRegionRequest) {
        if (!validation.validate()) return

        showSuccessOrFailed(
            context,
            LimeController.regionUpsert(data),
            data.id === 0 ? context.word('region_added') : context.word('region_updated')
        ).then(() => {
            pagedTableRef.current?.refresh()
            setShow(false)
        })
    }

    const validation = useValidation({
        nameRequired: () => upsertData.name.length > 0
    })

    return (
        <>
            <Dialog
                mounted={focusFirstInput}
                title={upsertData.id === 0 ? context.word('add_region') : context.word('edit_region')}
                show={show}
                setShow={setShow}
                body={
                    <EditTable
                        save={() => upsertCallback(upsertData)}
                        discard={() => setShow(false)}
                        saveWord={upsertData.id === 0 ? 'insert' : 'update'}
                    >
                        {EditRow(
                            context.word('name'),
                            <Input value={upsertData.name} change={v => setData({ name: v })} />,
                            validation.rules.nameRequired,
                            context.word('name_required')
                        )}
                        {EditRow(
                            context.word('lab_input_unit'),
                            <SelectString
                                options={labInputUnitOptions}
                                textFunc={u => labInputUnitDisplay(u.id as LabInputUnit, system)}
                                valueFunc={u => u.id}
                                value={upsertData.labInputUnit}
                                onChange={v => setData({ labInputUnit: v as LabInputUnit })}
                            />
                        )}
                    </EditTable>
                }
            />

            <div className="btn bg-primary m-2" onClick={() => showPopup(emptyRegion)}>
                {context.word('new_region')}
            </div>

            <PagedSearchTable<LimingRegionResponse>
                componentRef={pagedTableRef}
                call={LimeController.regionsPaged}
                columns={[
                    { header: context.word('name'), row: item => item.name },
                    {
                        header: context.word('lab_input_unit'),
                        row: item => labInputUnitDisplay(item.labInputUnit, system)
                    },
                    {
                        header: context.word('action'),
                        row: item => (
                            <span
                                className="underline cursor-pointer text-primary-600"
                                onClick={() => showPopup(toForm(item))}
                            >
                                {context.word('edit')}
                            </span>
                        )
                    }
                ]}
                keyExtractor={i => i.id}
            />
        </>
    )
}

export default LimingRegions
